import type { Route } from "./+types/home";
import { brandName } from "@mealmanager/legal-content";
import { useLoaderData } from "react-router";
import * as Sentry from "@sentry/node";
import Page from "../homepage/page";
import { buildSeoMeta } from "../utils/seo";
import { fetchConfigForRequest } from "../utils/configLoader";
import type { ConfigValues } from "../components/Config";
import { getBlogFlags } from "../utils/blogFlags";
import { getRecentPosts, type BlogPostSummary } from "../blog/blog.server";

export const meta: Route.MetaFunction = ({ location }) =>
  buildSeoMeta({
    location,
    title: `${brandName} | Real food, made simple`,
    description:
      `Unprocess your plate with ${brandName}. Discover whole-food recipes, personalised meal plans, and an AI Sous Chef that tracks nutrition beyond calories.`,
  });

type LoaderData = {
  config: ConfigValues | null;
  posts: BlogPostSummary[];
  showBlogLink: boolean;
};

export async function loader({ request, context }: Route.LoaderArgs) {
  const config = await fetchConfigForRequest(request, context);
  let posts: BlogPostSummary[] = [];
  const { blogEnabled, blogHomeFeedEnabled } = getBlogFlags(config);
  // Without the feed flag, or if Sanity fails, the homepage falls back to a plain link to /blog.
  let showBlogLink = blogEnabled && !blogHomeFeedEnabled;
  if (blogEnabled && blogHomeFeedEnabled) {
    try {
      posts = await getRecentPosts();
    } catch (error) {
      showBlogLink = true;
      Sentry.captureException(error, { fingerprint: ["blog-home-feed-unavailable"] });
      console.warn("[blog] Homepage post feed unavailable", error);
    }
  }
  const payload: LoaderData = { config, posts, showBlogLink };
  return Response.json(payload);
}

export default function Home() {
  const { config, posts, showBlogLink } = useLoaderData<typeof loader>();
  return <Page initialConfig={config ?? undefined} posts={posts} showBlogLink={showBlogLink} />;
}
